/**
 * THE VIDEO'S COVER (v1.0.29): a frame of the video, the author's own picture, or - for a YouTube
 * video - YouTube's own still. A frame and the still are made into a cover by the server
 * (`setVideoCover`), which keeps a vertical cover vertical; an own picture goes through the same
 * claim as an article's cover. Either way the answer is named on the draft by the next autosave
 * (`onCover`, the editor's `setCover`), exactly as after a cover upload.
 *
 * The preview is the card's shape for this video - 9:16 or 16:9 - so the author sees the crop
 * readers will.
 *
 * ★ A COVER BEING MADE HOLDS SEND, as a picture going in does (../articles/editor/uploads-in-flight.ts):
 * a Send meanwhile would seal the old cover, and the answer would then land on a copy nobody may
 * change. And once it is the cover, a polite status line says so, as the own picture's does.
 */
import { Button, Group, Stack, Text, Title } from '@mantine/core';
import { useState } from 'react';
import { useEditorApi } from '../../features/cabinet/api/editor-api';
import type { ArticleVideo, StoredImage } from '../../features/cabinet/api/editor-api';
import { useAction } from '../../features/cabinet/state';
import { emulatorImageUrl, servedUrlOnError } from '../../features/storage/emulator-image-url';
import { useTranslation } from '../../i18n';
import { ImageUploadButton } from '../articles/editor/ImageUploadButton';
import { useUploadsInFlight } from '../articles/editor/uploads-in-flight';
import { describeCoverFailure } from './video-words';
import classes from './video.module.css';

export interface VideoCoverProps {
  readonly articleId: string;
  readonly video: ArticleVideo | null;
  /** The cover the draft holds now, if any. */
  readonly coverUrl: string | null;
  readonly disabled: boolean;
  readonly onCover: (image: StoredImage) => void;
}

export function VideoCover({ articleId, video, coverUrl, disabled, onCover }: VideoCoverProps) {
  const { t } = useTranslation('video');
  const api = useEditorApi();
  const uploads = useUploadsInFlight();
  const [frame, setFrame] = useState<number | null>(null);
  // Whether the last frame or still asked for became the cover, for the status line.
  const [made, setMade] = useState(false);
  const make = useAction(async (body: { frame: number } | { youtubeThumbnail: true }) =>
    api.setVideoCover(articleId, body),
  );
  const orientation = video?.orientation ?? 'horizontal';
  const frames =
    video?.source === 'upload' && video.state === 'ready' ? (video.frameUrls ?? []) : [];
  const youtube = video?.source === 'youtube' && video.state === 'ready';

  const use = (body: { frame: number } | { youtubeThumbnail: true }) => {
    setMade(false);
    // Counted in until the answer is on the draft, not merely back.
    const release = uploads.hold();
    void (async () => {
      try {
        const image = await make.run(body);
        if (image !== undefined) {
          onCover(image);
          setMade(true);
        }
      } finally {
        release();
      }
    })();
  };

  return (
    <section className={classes.section} aria-labelledby="video-cover-title">
      <Stack gap={2}>
        <Title id="video-cover-title" order={2} size="h5">
          {t('cover.title')}
        </Title>
        <Text className={classes.hint}>{t('cover.note')}</Text>
      </Stack>

      <div className={classes.coverPreview} data-orientation={orientation}>
        {coverUrl === null ? (
          <span>{t('cover.none')}</span>
        ) : (
          <img
            className={classes.coverImage}
            src={emulatorImageUrl(coverUrl)}
            onError={servedUrlOnError(coverUrl)}
            alt={t('cover.previewAlt')}
          />
        )}
      </div>

      {frames.length > 0 ? (
        <Stack gap="xs">
          <Text size="sm" fw={600} id="video-cover-frames">
            {t('cover.chooseFrame')}
          </Text>
          <ul
            className={classes.frames}
            data-orientation={orientation}
            aria-labelledby="video-cover-frames"
          >
            {frames.map((url, index) => (
              <li key={index}>
                <button
                  type="button"
                  className={classes.frameButton}
                  aria-pressed={frame === index}
                  aria-label={t('cover.frame', { number: String(index + 1) })}
                  disabled={disabled}
                  onClick={() => {
                    setFrame(index);
                  }}
                >
                  <img className={classes.frameImage} src={url} alt="" />
                </button>
              </li>
            ))}
          </ul>
          <div>
            <Button
              size="compact-sm"
              disabled={disabled || frame === null}
              loading={make.isRunning}
              onClick={() => {
                if (frame !== null) {
                  use({ frame });
                }
              }}
            >
              {t('cover.useFrame')}
            </Button>
          </div>
        </Stack>
      ) : youtube ? null : (
        <Text className={classes.hint}>{t('cover.framesLater')}</Text>
      )}

      <Group gap="xs" align="flex-start">
        {youtube ? (
          <Button
            variant="default"
            size="compact-sm"
            disabled={disabled}
            loading={make.isRunning}
            onClick={() => {
              use({ youtubeThumbnail: true });
            }}
          >
            {t('cover.useYoutube')}
          </Button>
        ) : null}
        <ImageUploadButton
          articleId={articleId}
          role="cover"
          label={t('cover.uploadOwn')}
          doneMessage={t('cover.done')}
          disabled={disabled}
          onClaimed={onCover}
        />
      </Group>

      {/* ALWAYS MOUNTED, EVEN EMPTY: a live region that appears with its first message may never
          be announced. */}
      <Text size="xs" c="dimmed" role="status">
        {made && !make.isRunning ? t('cover.done') : ''}
      </Text>

      {make.error === undefined ? null : (
        <Text size="sm" role="alert" style={{ color: 'oklch(var(--mx-danger))' }}>
          {t('cover.notMade')} {describeCoverFailure(make.error, t)}
        </Text>
      )}
    </section>
  );
}
